import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { ROUTE_PARENT, labelFor } from './routeLabels';
import { useBreadcrumbContext } from './BreadcrumbContext';

// Role → home route mapping (mirrors Header.jsx goHome)
const ROLE_HOME = {
  platform_admin: '/dashboard',
  school_admin: '/dashboard',
  teacher: '/dashboard',
  parent: '/parent-dashboard',
  student: '/dashboard',
};

// Routes that are themselves "home" / top-level role landing pages — no Back bar.
const HOME_ROUTES = new Set([
  '/dashboard',
  '/school-dashboard',
  '/parent-dashboard',
  '/',
]);

// Build the breadcrumb trail by walking up ROUTE_PARENT until we hit a home/role route.
function buildTrail(path) {
  const visited = new Set();
  const trail = [];
  let cursor = path;
  while (cursor && !HOME_ROUTES.has(cursor) && !visited.has(cursor)) {
    visited.add(cursor);
    trail.unshift(cursor);
    cursor = ROUTE_PARENT[cursor] || null;
  }
  return trail;
}

export default function BackBar({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const breadcrumb = useBreadcrumbContext();
  const extras = breadcrumb?.extras || [];
  const replaceCurrent = !!breadcrumb?.replaceCurrent;

  const path = location.pathname;
  if (HOME_ROUTES.has(path)) return null;

  const home = ROLE_HOME[user?.role] || '/dashboard';
  const trail = buildTrail(path);

  const handleBack = () => {
    if (window.history.length > 2 && document.referrer && document.referrer.includes(window.location.host)) {
      navigate(-1);
    } else {
      navigate(home);
    }
  };

  // Compose final segments. If `replaceCurrent`, drop the auto-generated last
  // segment and use dynamic extras instead. Otherwise append extras after the trail.
  const baseSegments = trail.map((segPath) => ({ label: labelFor(segPath, language), path: segPath }));
  const composed = (() => {
    if (extras.length === 0) return baseSegments;
    if (replaceCurrent && baseSegments.length > 0) {
      return [...baseSegments.slice(0, -1), ...extras];
    }
    return [...baseSegments, ...extras];
  })();

  return (
    <div className="bg-white border-b border-gray-200 px-3 sm:px-6 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="global-back-bar">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <button
          onClick={handleBack}
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-orange-600 transition-colors px-2.5 py-1.5 rounded-md hover:bg-orange-50 shrink-0"
          data-testid="back-button"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>{language === 'hi' ? 'वापस' : 'Back'}</span>
        </button>

        {/* Breadcrumb trail */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs sm:text-sm overflow-x-auto min-w-0" data-testid="breadcrumb-trail">
          <button
            onClick={() => navigate(home)}
            className="flex items-center gap-1 text-slate-500 hover:text-orange-600 transition-colors shrink-0"
            data-testid="breadcrumb-home"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7m-9 2v8a1 1 0 001 1h2m4-9l2 2m-2-2v8a1 1 0 01-1 1h-2m-4 0h4" />
            </svg>
            <span>{language === 'hi' ? 'होम' : 'Home'}</span>
          </button>

          {composed.map((seg, idx) => {
            const isLast = idx === composed.length - 1;
            const segKey = seg.path || `extra-${idx}-${seg.label}`;
            const testIdSlug = (seg.path || `extra-${idx}`).replace(/\//g, '');
            return (
              <React.Fragment key={segKey}>
                <span className="text-slate-300 shrink-0" aria-hidden="true">›</span>
                {isLast || !seg.path ? (
                  <span
                    className={isLast ? 'font-semibold text-slate-800 truncate' : 'text-slate-500 truncate'}
                    aria-current={isLast ? 'page' : undefined}
                    data-testid={isLast ? `breadcrumb-current-${testIdSlug}` : `breadcrumb-static-${testIdSlug}`}
                  >
                    {seg.label}
                  </span>
                ) : (
                  <button
                    onClick={() => navigate(seg.path)}
                    className="text-slate-500 hover:text-orange-600 transition-colors truncate"
                    data-testid={`breadcrumb-link-${testIdSlug}`}
                  >
                    {seg.label}
                  </button>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
